import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  TablePagination,
  Grid,
  Typography,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  Security,
} from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { apiService } from '../../services/apiService';
import { useAppSelector } from '../../store/hooks';
import { useNavigate } from 'react-router-dom';
import SettingsSidebar from '../../components/Common/SettingsSidebar';

interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  permissions?: Permission[];
  created_at: string;
  updated_at: string;
}

interface Permission {
  id: number;
  name: string;
  display_name: string;
  module: string;
}

const Roles: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await apiService.getRoles();
      setRoles(response.data.data || response.data);
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      toast.error(error.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = filteredRoles.map((role) => role.id);
      setSelectedRoles(newSelected);
      return;
    }
    setSelectedRoles([]);
  };

  const handleSelectClick = (roleId: number) => {
    const selectedIndex = selectedRoles.indexOf(roleId);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRoles, roleId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRoles.slice(1));
    } else if (selectedIndex === selectedRoles.length - 1) {
      newSelected = newSelected.concat(selectedRoles.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRoles.slice(0, selectedIndex),
        selectedRoles.slice(selectedIndex + 1)
      );
    }

    setSelectedRoles(newSelected);
  };

  const handleDeleteRole = async (roleId: number) => {
    const role = roles.find(r => r.id === roleId);
    const roleName = role ? role.display_name || role.name : 'this role';
    
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${roleName}. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await apiService.deleteRole(roleId);
        
        Swal.fire({
          title: 'Deleted!',
          text: 'Role has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#3085d6',
        });
        
        fetchRoles();
      } catch (error: any) {
        console.error('Error deleting role:', error);
        
        Swal.fire({
          title: 'Error!',
          text: error.message || 'Failed to delete role',
          icon: 'error',
          confirmButtonColor: '#3085d6',
        });
      }
    }
  };

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const paginatedRoles = filteredRoles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getRolePermissionsCount = (role: Role) => {
    return role.permissions ? role.permissions.length : 0;
  };

  const getRoleStatusColor = (roleName: string) => {
    switch (roleName.toLowerCase()) {
      case 'admin':
        return '#f44336';
      case 'manager':
        return '#ff9800';
      case 'employee':
        return '#4caf50';
      case 'viewer':
        return '#2196f3';
      default:
        return '#9e9e9e';
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      <ToastContainer />
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '2rem' }}>
          Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<Security />}
          onClick={() => navigate('/roles/add')}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: '#4A90E2',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          New Role
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Sidebar - Settings Menu */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar />
        </Grid>

        {/* Right Content Area */}
        <Grid size={{ xs: 12, md: 9 }}>

          {/* Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    mb: 1
                  }}>
                    Total Roles
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    color: '#1e293b', 
                    fontWeight: 700,
                    fontSize: '2rem'
                  }}>
                    {roles.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    mb: 1
                  }}>
                    Active Roles
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    color: '#1e293b', 
                    fontWeight: 700,
                    fontSize: '2rem'
                  }}>
                    {roles.filter(role => role.name !== 'deleted').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    mb: 1
                  }}>
                    System Roles
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    color: '#1e293b', 
                    fontWeight: 700,
                    fontSize: '2rem'
                  }}>
                    {roles.filter(role => ['admin', 'manager', 'employee', 'viewer'].includes(role.name)).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search and Filter Section */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            gap: 2,
            mb: 3,
            p: 3,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
              <TextField
                placeholder="Search roles..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#9ca3af' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    height: '48px',
                  },
                }}
              />
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                sx={{
                  borderRadius: '8px',
                  height: '48px',
                  px: 2,
                  borderColor: '#d1d5db',
                  color: '#374151',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f9fafb',
                  },
                }}
              >
                Filter
              </Button>
            </Box>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              sx={{
                borderRadius: '8px',
                height: '48px',
                px: 2,
                borderColor: '#d1d5db',
                color: '#374151',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb',
                },
              }}
            >
              Export
            </Button>
          </Box>

          {/* Roles Table */}
          <Card sx={{ 
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <TableContainer sx={{ height: 'calc(100vh - 450px)' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedRoles.length > 0 && selectedRoles.length < filteredRoles.length}
                          checked={filteredRoles.length > 0 && selectedRoles.length === filteredRoles.length}
                          onChange={handleSelectAllClick}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>Display Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>Permissions</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRoles.map((role) => (
                      <TableRow key={role.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRoles.indexOf(role.id) !== -1}
                            onChange={() => handleSelectClick(role.id)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{role.display_name}</TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>
                          {role.description || 'No description'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#374151' }}>
                            {getRolePermissionsCount(role)} permissions
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>
                          {new Date(role.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate(`/roles/edit/${role.id}`)}
                              sx={{
                                borderRadius: '6px',
                                textTransform: 'none',
                                fontSize: '0.875rem',
                                borderColor: '#d1d5db',
                                color: '#374151',
                                '&:hover': {
                                  borderColor: '#9ca3af',
                                  backgroundColor: '#f9fafb',
                                },
                              }}
                            >
                              Update
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleDeleteRole(role.id)}
                              sx={{
                                borderRadius: '6px',
                                textTransform: 'none',
                                fontSize: '0.875rem',
                                borderColor: '#f87171',
                                color: '#dc2626',
                                '&:hover': {
                                  borderColor: '#ef4444',
                                  backgroundColor: '#fef2f2',
                                },
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredRoles.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid #e5e7eb',
                  '& .MuiTablePagination-toolbar': {
                    paddingLeft: 3,
                    paddingRight: 3,
                  },
                }}
              />
            </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Roles;
